<template>
  <div class="section section_white">
    <div class="max-width">
      <div class="themes">
        <div class="section__head section__head_row">
          <div class="section__about">
            <h2 class="section__title">Featured Themes</h2>
            <div class="section__text">Build your store in hours with conversion-optimized themes from Creative Commerce.</div>
          </div>
          <div class="section__buttons">
            <router-link to="/themes" class="button">
              View All
              <IconArrow/>
            </router-link>
          </div>
        </div>

        <div class="themes__list">
          <div
              v-for="theme in themesFiltered"
              :key="theme.id"
              class="themes__item fadeIn"
          >
            <router-link
                :to="`/themes/${theme.id}`"
                class="themes__pic"
                :class="`themes__pic_${theme.id}`"
            >
              <img loading="lazy" :srcset="theme.image.srcset" :src="theme.image.src" :alt="theme.image.alt" />
            </router-link>

            <div class="themes__about">
              <div v-if="theme.featured" class="themes__popular">
                <IconPopular/>
                Featured Theme
              </div>

              <h3 class="themes__title"><router-link :to="`/themes/${theme.id}`">{{ theme.title }}</router-link></h3>
              <div class="themes__desc">{{ theme.desc }}</div>

              <div class="themes__info">
                <div class="themes__label">Optimized for</div>
                <div class="themes__tags">
                  <div
                      v-for="tag in theme.tags"
                      :key="tag"
                      class="themes__tags-item"
                  >
                    {{ tag }}
                  </div>
                </div>
              </div>

              <div class="themes__actions">
                <router-link :to="`/themes/${theme.id}`" class="button">
                  Explore {{ theme.title }}
                  <IconArrow/>
                </router-link>

                <a :href="theme.shopifyLink" class="button button_border" target="_blank">
                  View on Shopify
                  <IconShopify/>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import IconArrow from "@/components/icons/IconArrow.vue";
import IconShopify from "@/components/icons/IconShopify.vue";
import IconPopular from "@/components/icons/IconPopular.vue";

const themes = ref([])

onMounted(async () => {
  const res = await fetch('/assets/json/themes.json')
  themes.value = await res.json()
})

const currentListId = 'featured'

const themesFiltered = computed(() => {
  return themes.value.filter(
      theme => !theme.hiddenIn?.includes(currentListId)
  )
})
</script>

<style lang="scss">
.themes {
  &__list {
    display: flex;
    gap: 4rem;
    flex-direction: column;

    @include lg {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }

    @include md {
      gap: 3rem;
      grid-template-columns: repeat(1, 1fr);
    }
  }

  &__item {
    display: flex;
    gap: 5.313rem;
    justify-content: space-between;
    align-items: flex-start;

    @include lg {
      gap: 2rem;
      flex-direction: column;
    }
  }

  &__pic {
    flex: auto;
    background: url('@/assets/imgs/themes-bg.webp') no-repeat;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background-size: cover;
    border-radius: 12px;
    box-shadow:inset 0 0 0 1px rgba(138,138,138,.1);
    padding: 4.5rem 4.5rem 0;

    &_mix {
      background: url('@/assets/imgs/themes-bg_mix.webp') no-repeat;
      background-size: cover;
    }

    &_force {
      background: url('@/assets/imgs/themes-bg_force.webp') no-repeat;
      background-size: cover;
    }

    @include lg {
      padding: 3rem 3rem 0;
    }

    @include md {
      width: 100%;
    }

    img {
      width: 100%;
      filter: drop-shadow(0 0 21px rgba(127, 127, 127, 0.1));
    }
  }

  &__about {
    flex: none;
    width: 478px;

    @include lg {
      width: 100%;
    }
  }

  &__popular {
    margin: 0 0 .75rem;
    display: flex;
    align-items: center;
    color: var(--color-accent);
    gap: 5px;
    font-size: .875rem;
  }

  &__title {
    font-size: 2.5rem;
    line-height: 1.2;
    font-weight: 900;

    a:hover {
      opacity:.7;
    }
  }

  &__desc {
    margin: 1.25rem 0 0;
    color: var(--color-gray);
  }

  &__info {
    border-top: 1px solid var(--color-border);
    margin-top: 1.25rem;
    padding-top: 1.25rem;
  }

  &__label {
    font-size: .875rem;
    margin: 0 0 .75rem;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-width: 310px;

    &-item {
      display: flex;
      align-items: center;
      background: var(--color-accent-light);
      color: var(--color-accent);
      height: 1.875rem;
      padding: 0 .75rem 1px;
      font-size: .875rem;
      border-radius: 40px;
      text-transform:capitalize;
    }
  }

  &__actions {
    display: flex;
    gap: .5rem;
    margin: 2.5rem 0 0;

    @include xs {
      flex-direction: column;
    }
  }
}
</style>
